import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { PawnNavIcon } from '@/components/help-center/MaskedSvgIcon';
import { PainelDashedAddCardButton } from '@/components/painel/cms/SmilieCodeField';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import type { PowerPawnInfo } from '@/lib/powers-data';
import { cn } from '@/lib/utils';

type PowerPawnsEditorProps = {
  value: PowerPawnInfo[];
  onChange: (next: PowerPawnInfo[]) => void;
};

function pawnPreviewCode(pawn: PowerPawnInfo): string {
  const name = pawn.name.trim();
  if (name) return name;
  const hat = pawn.hat.trim();
  const fromHat = /#([A-Za-z0-9_]+)/.exec(hat)?.[1];
  return fromHat || hat.replace(/[()#]/g, '') || 'pawn';
}

export function PowerPawnsEditor({ value, onChange }: PowerPawnsEditorProps) {
  const patch = (index: number, patchRow: Partial<PowerPawnInfo>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patchRow } : row)));
  };

  const add = () => {
    onChange([...value, { hat: '', name: '' }]);
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <p className="font-inter text-sm text-slate-400">
          Nenhum peão listado. Os peões entram automaticamente pela API ao criar o power, ou
          adicione manualmente.
        </p>
      ) : (
        <div className="space-y-2">
          {value.map((pawn, index) => (
            <div
              key={index}
              className={cn(
                'grid gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3',
                'sm:grid-cols-[auto_1fr_1fr_auto] sm:items-end'
              )}
            >
              <div className="flex items-center justify-center sm:pb-0.5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                  <GsImage
                    code={pawnPreviewCode(pawn)}
                    className="flex h-10 w-10 items-center justify-center"
                    imgClassName="max-h-9 max-w-9 object-contain"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-slate-500">Smiley interno (pré-visualização)</Label>
                <Input
                  value={pawn.name}
                  onChange={(e) => patch(index, { name: e.target.value })}
                  className={cn(painelInputClass, 'font-mono text-xs')}
                  placeholder="everypawnpink"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-slate-500">Código hat</Label>
                <Input
                  value={pawn.hat}
                  onChange={(e) => patch(index, { hat: e.target.value })}
                  className={cn(painelInputClass, 'font-mono text-xs')}
                  placeholder="(hat#Ekh)"
                />
              </div>
              <div className="flex items-end justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="rounded-xl text-rose-300 hover:bg-rose-500/15 hover:text-rose-100"
                  onClick={() => remove(index)}
                  aria-label="Remover peão"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <PainelDashedAddCardButton
        label="Adicionar peão"
        onClick={add}
        accent="cyan"
        icon={<PawnNavIcon className="h-6 w-6" />}
      />
    </div>
  );
}
